import styled from '@emotion/styled/macro';
import {
  Button,
  Card,
  CardHeader,
  HqsStatus,
  Pill,
  PipelineUrgentActionBanner,
  SfdcEnv,
  SlidingDrawer,
  SvgIcon,
  SvgNames,
  tokens,
  Typography,
  useSingletonMobileMediaQuery,
  NotOptimizedForMobileModal,
  useSingletonTabletMediaQuery,
  MOBILE_BREAKPOINT,
} from '@sunrun/experience-ui-components';
import {
  FIELD_SALES,
  FUSION_CLOSER,
  FUSION_SETTER,
  getEnvironment,
  getSfdcEnv,
  INSIDE_SALES,
  CX_PRO,
} from '@sunrun/sales-experience-shared';
import {
  useFeatureFlagEnabled,
  useFeatureFlagString,
} from '@sunrun/sales-experience-shared/react-feature-flags';
import {
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { ChatIframe } from '../../atoms/ChatIframe';
// eslint-disable-next-line import/no-restricted-paths
import { PipelineCardInfoItem } from './PipelineCardInfoItem';
import { AppointmentView } from 'components/atoms/AppointmentView';
// eslint-disable-next-line import/no-restricted-paths
import { SharePresentationModal } from 'components/organisms/SharePresentationModal';
// eslint-disable-next-line import/no-restricted-paths
import { ShareBillModal } from 'components/organisms/ShareBillModal';
import {
  formatCurrency,
  formatDate,
  formatPipelineCardAddress,
  formatTime,
} from 'helpers/formatHelpers';
import {
  getProspectCommissionAmount,
  stageShowsCommissionTotal,
} from 'helpers/pipelineCommissionHelpers';
import { isAffiliateHelper } from 'helpers/repTypeHelper';
import { useHybridToken } from 'providers/Auth';
import { OpenCustomerProfileOptions } from 'helpers/listViewHelpers';
import { getHQS } from 'providers/HQS';
import {
  deletePipelineCache,
  PipelineProspect,
  updateFavorite,
} from 'providers/Pipeline';
import { useUserProfile } from 'providers/UserProfile';
import {
  CHECKOUT,
  NEW_CUSTOMER,
  OFFER_CREATED,
  PIPELINE_DRAWER_HOST_ID,
} from 'components/atoms/PipelineConsts';
import { FEATURE_FLAGS } from 'constants/FeatureFlags';

const SPECIALIST_COMMISSION_CUTOFF = new Date('2026-04-21T00:00:00.000Z');

type PipelineCardProps = {
  pipelineProspect: PipelineProspect;
  includeSalesRep: boolean;
  showCommission: boolean;
  allowJobCode: boolean;
  numUnreadChats: number;
  showInstallScheduleModalAndGetUrl: (prospect: PipelineProspect) => void;
  openCustomerProfile: (
    prospectId: string,
    options?: OpenCustomerProfileOptions,
  ) => void;
  splatBaseUrl: string;
  oneBaseUrl: string;
  salesforceBaseUrl: string;
  sfPartnerBaseUrl: string;
  sfdcEnv: SfdcEnv;
};

const PipelineCardV2 = memo(
  ({
    pipelineProspect,
    includeSalesRep,
    showCommission,
    allowJobCode,
    numUnreadChats: initialNumUnreadChats,
    showInstallScheduleModalAndGetUrl,
    openCustomerProfile,
    splatBaseUrl,
    oneBaseUrl,
    salesforceBaseUrl,
    sfPartnerBaseUrl,
    sfdcEnv,
  }: PipelineCardProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const [isChatOpen, setIsChatOpen] = useState(false);
    const [drawerHost, setDrawerHost] = useState<HTMLElement | null>(null);
    const [isFavoriteSaving, setIsFavoriteSaving] = useState(false);
    const [numUnreadChats, setNumUnreadChats] = useState(initialNumUnreadChats);
    useEffect(() => {
      setNumUnreadChats(initialNumUnreadChats);
    }, [initialNumUnreadChats]);
    useLayoutEffect(() => {
      setDrawerHost(document.getElementById(PIPELINE_DRAWER_HOST_ID));
    }, []);
    const [hqsStatus, setHqsStatus] = useState<HqsStatus>(
      pipelineProspect.pipelineStage === NEW_CUSTOMER ||
        pipelineProspect.pipelineStage === OFFER_CREATED
        ? 'LOADING'
        : 'INELIGIBLE',
    );
    const [isNotMobileOptimizedModalOpen, setIsNotMobileOptimizedModalOpen] =
      useState(false);
    const [isSharePresentationOpen, setIsSharePresentationOpen] =
      useState(false);
    const [isShareBillOpen, setIsShareBillOpen] = useState(false);
    const env = getEnvironment();

    const { userProfile, splatUserType } = useUserProfile();
    const { hybridToken, hybridTokenType } = useHybridToken();

    const isFusionExpert =
      userProfile?.sellsSunrun === 'Yes' && userProfile?.isFusion;
    const isMobile = useSingletonMobileMediaQuery();
    const isTablet = useSingletonTabletMediaQuery();

    const configuredPartnerSfdcUrl = useFeatureFlagString(
      FEATURE_FLAGS.SFDC_PARTNER_URL,
    );
    const configuredSfdcUrl = useFeatureFlagString(FEATURE_FLAGS.SFDC_URL);
    const isSalesChatEnabled = useFeatureFlagEnabled(
      FEATURE_FLAGS.SALES_CHAT_ENABLED,
    );
    const isNewPresentationEnabled = useFeatureFlagEnabled(
      FEATURE_FLAGS.NEW_PRESENTATION,
    );
    const {
      salesforceUrl,
      canUseAppointments,
      useJobCode,
      isOwner,
      useFusionCommission,
      projectUrl,
    } = useMemo(() => {
      if (!userProfile) return {};

      const isOwner = pipelineProspect.salesRepId === userProfile.contactId;

      let salesforceUrl;
      let projectUrl = undefined;
      switch (getSfdcEnv(userProfile)) {
        case 'Community': {
          const baseUrl =
            configuredPartnerSfdcUrl ??
            (env === 'production'
              ? 'https://sunrun.my.site.com'
              : 'https://sunrun--uat.sandbox.my.site.com');
          const sfdcBaseUrl = `${baseUrl}/partners`;
          salesforceUrl = `${sfdcBaseUrl}/s/partnerOpp?id=${pipelineProspect.opportunityId}`;
          break;
        }
        case 'D2HCommunity': {
          const baseUrl =
            configuredPartnerSfdcUrl ??
            (env === 'production'
              ? 'https://sunrun.my.site.com'
              : 'https://sunrun--uat.sandbox.my.site.com');
          const sfdcBaseUrl = `${baseUrl}/d2hpartnercommunity`;
          salesforceUrl = `${sfdcBaseUrl}/PartnerOpportunityCreate?id=${pipelineProspect.opportunityId}`;
          if (pipelineProspect.projectId) {
            projectUrl = `${sfdcBaseUrl}/WFProjectRedirectPage?id=${
              pipelineProspect.projectId
            }${isOwner ? '' : '&mgrView=1'}`;
          }
          break;
        }
        case 'SFDC':
        case 'None':
        default: {
          const sfdcBaseUrl =
            configuredSfdcUrl ??
            (env === 'production'
              ? 'https://sunrun.my.salesforce.com'
              : 'https://sunrun--uat.sandbox.my.salesforce.com');
          salesforceUrl = `${sfdcBaseUrl}/${pipelineProspect.opportunityId}`;
          if (pipelineProspect.projectId) {
            projectUrl = `${sfdcBaseUrl}/${pipelineProspect.projectId}`;
          }
          break;
        }
      }

      const canUseAppointments =
        userProfile?.splatRole === INSIDE_SALES ||
        userProfile?.splatRole === FIELD_SALES ||
        userProfile?.splatRole === FUSION_CLOSER ||
        userProfile?.splatRole === FUSION_SETTER;

      const useJobCode = pipelineProspect.jobCode && allowJobCode;

      const useFusionCommission = userProfile.isFusion;
      return {
        salesforceUrl,
        canUseAppointments,
        useJobCode,
        isOwner,
        useFusionCommission,
        projectUrl,
      };
    }, [
      allowJobCode,
      configuredPartnerSfdcUrl,
      configuredSfdcUrl,
      pipelineProspect,
      userProfile,
      env,
    ]);

    const daysAgoText = useMemo(() => {
      const daysAgo = Math.floor(
        (new Date().getTime() -
          Date.parse(pipelineProspect.statusDate + '-0000')) /
          1000 /
          60 /
          60 /
          24,
      );
      if (daysAgo < 1) {
        return 'Today';
      }
      if (daysAgo === 1) {
        return '1 day';
      }
      return `${daysAgo} days`;
    }, [pipelineProspect.statusDate]);

    const starClick = useCallback(() => {
      setIsFavoriteSaving(true);
      updateFavorite(
        hybridToken,
        pipelineProspect,
        !pipelineProspect.starredInPipeline,
      )
        .then(() =>
          pipelineProspect.salesRepId
            ? deletePipelineCache(hybridToken, pipelineProspect.salesRepId)
            : undefined,
        )
        .then(() => {
          pipelineProspect.starredInPipeline =
            !pipelineProspect.starredInPipeline;
        })
        .catch(() => {
          // Do nothing
        })
        .finally(() => setIsFavoriteSaving(false));
    }, [hybridToken, pipelineProspect]);

    const handleChatClick = () => {
      setIsChatOpen(true);
      setNumUnreadChats(0);
    };

    const handleShowInstallSchedule = useCallback(
      () => showInstallScheduleModalAndGetUrl(pipelineProspect),
      [showInstallScheduleModalAndGetUrl, pipelineProspect],
    );

    const viewCapNextSteps = useCallback(
      () => openCustomerProfile(pipelineProspect.prospectId),
      [openCustomerProfile, pipelineProspect.prospectId],
    );

    const viewPendingTask = useCallback(
      () =>
        openCustomerProfile(pipelineProspect.prospectId, {
          openPendingTask: true,
        }),
      [openCustomerProfile, pipelineProspect.prospectId],
    );

    const markClosedLost = useCallback(
      () =>
        openCustomerProfile(pipelineProspect.prospectId, {
          markClosedLost: true,
        }),
      [openCustomerProfile, pipelineProspect.prospectId],
    );

    const mobileOpenOfferPresentation = useCallback(() => {
      setIsNotMobileOptimizedModalOpen(true);
    }, []);

    const openOfferPresentation = useCallback(() => {
      const { username, email, splatRole } = userProfile ?? {};
      const userName = encodeURIComponent(username ?? '');
      const userEmail = encodeURIComponent(email ?? '');
      const userRole = encodeURIComponent(splatRole ?? '');

      const redirectUrl = encodeURIComponent(
        `/go-solar/${pipelineProspect.prospectId}?prospectId=${pipelineProspect.prospectId}&username=${userName}&email=${userEmail}&role=${userRole}`,
      );
      const url = `${splatBaseUrl}/login?token=${hybridToken}&tokenType=hybrid-${hybridTokenType}&redirect=${redirectUrl}`;
      window.open(url, '_blank');
      setIsNotMobileOptimizedModalOpen(false);
    }, [
      hybridToken,
      hybridTokenType,
      pipelineProspect,
      splatBaseUrl,
      userProfile,
    ]);

    const openNewOfferPresentation = useCallback(() => {
      const redirectUrl = encodeURIComponent(
        `/slideshow/${pipelineProspect.prospectId}`,
      );
      const url = `https://staging.presentation.sunrunone.com/transfer?token=${hybridToken}&tokenType=hybrid-${hybridTokenType}&redirect=${redirectUrl}`;
      window.open(url, '_blank');
      setIsNotMobileOptimizedModalOpen(false);
    }, [hybridToken, hybridTokenType, pipelineProspect]);

    const navigateToCustomerProfile = useCallback(() => {
      window.open(
        `${oneBaseUrl}/customers/customer-profile/${pipelineProspect.prospectId}`,
        '_blank',
      );
    }, [oneBaseUrl, pipelineProspect.prospectId]);

    const emailCustomer = useCallback(() => {
      window.open(`mailto:${pipelineProspect.email}`, '_blank');
    }, [pipelineProspect]);

    const viewInSalesforce = useCallback(() => {
      window.open(salesforceUrl, '_blank');
    }, [salesforceUrl]);

    const viewProject = useCallback(() => {
      window.open(projectUrl, '_blank');
    }, [projectUrl]);

    const copyFieldValue = useCallback((value: string) => {
      navigator.clipboard.writeText(value);
    }, []);

    const isOnOrAfterSpecialistCommissionCutoff =
      pipelineProspect.opportunityCreatedDate != null &&
      new Date(pipelineProspect.opportunityCreatedDate) >=
        SPECIALIST_COMMISSION_CUTOFF;

    const showSpecialistCommissions =
      isFusionExpert &&
      pipelineProspect.fusionCommissionSpecialist !== null &&
      isOnOrAfterSpecialistCommissionCutoff;

    const showCommissionPills =
      showCommission &&
      stageShowsCommissionTotal(pipelineProspect.pipelineStage);

    const commissionAmount = getProspectCommissionAmount(
      pipelineProspect,
      useFusionCommission ?? false,
    );

    const specialistCommissionAmount = useFusionCommission
      ? pipelineProspect.fusionCommissionSpecialist
      : pipelineProspect.fusionCommission;

    const canSendShareBill = useMemo(() => {
      if (!isOwner) {
        return false;
      }

      return (
        pipelineProspect.pipelineStage === NEW_CUSTOMER &&
        !pipelineProspect.hasUtilityBill
      );
    }, [isOwner, pipelineProspect]);

    const canSendOfferPresentation = useMemo(() => {
      if (!isOwner) {
        return false;
      }

      switch (pipelineProspect.pipelineStage) {
        case OFFER_CREATED:
        case CHECKOUT:
          return true;
        default:
          return false;
      }
    }, [isOwner, pipelineProspect]);

    const isAffiliate = userProfile ? isAffiliateHelper(userProfile) : false;

    useEffect(() => {
      if (isOpen && hqsStatus === 'LOADING') {
        getHQS(hybridToken, pipelineProspect.prospectId)
          .then((result) => setHqsStatus(result))
          .catch(() => {
            // Do nothing.
          });
      }
    }, [hqsStatus, hybridToken, isOpen, pipelineProspect.prospectId]);

    const siteSurveyAppointment = useMemo(() => {
      const siteSurveyAppointments =
        pipelineProspect.serviceAppointments
          ?.filter((appointment) => appointment.recordType === 'Site Survey')
          .sort((a, b) => {
            return (
              new Date(a.arrivalWindowStartTime).getTime() -
              new Date(b.arrivalWindowStartTime).getTime()
            );
          }) ?? [];
      return siteSurveyAppointments.length > 0
        ? siteSurveyAppointments[0]
        : null;
    }, [pipelineProspect]);

    const isPreSale = [NEW_CUSTOMER, OFFER_CREATED, CHECKOUT].includes(
      pipelineProspect.pipelineStage,
    );

    return (
      <>
        {isNotMobileOptimizedModalOpen && (
          <NotOptimizedForMobileModal
            onContinueAnyway={openOfferPresentation}
            onClose={() => setIsNotMobileOptimizedModalOpen(false)}
          />
        )}
        {isShareBillOpen && (
          <ShareBillModal
            hybridToken={hybridToken}
            prospectId={pipelineProspect.prospectId}
            onDismiss={() => setIsShareBillOpen(false)}
            name={`${pipelineProspect.firstName} ${pipelineProspect.lastName}`}
            email={pipelineProspect.email}
            isAffiliate={isAffiliate}
          />
        )}
        {isSharePresentationOpen && (
          <SharePresentationModal
            hybridToken={hybridToken}
            prospectId={pipelineProspect.prospectId}
            onDismiss={() => setIsSharePresentationOpen(false)}
            name={`${pipelineProspect.firstName} ${pipelineProspect.lastName}`}
            email={pipelineProspect.email}
            isAffiliate={isAffiliate}
          />
        )}
        <PipelineStyledCard key={pipelineProspect.key}>
          <PipelineCardHeader>
            <TitleLine>
              <TitleText>
                {pipelineProspect.firstName} {pipelineProspect.lastName}
              </TitleText>
              {splatUserType !== CX_PRO && (
                <MenuButton
                  data-testid="star-button"
                  color="tertiary"
                  onClick={starClick}
                  disabled={isFavoriteSaving}
                  iconMiddle={
                    <SvgIcon
                      name={
                        isFavoriteSaving
                          ? SvgNames.Loading
                          : pipelineProspect.starredInPipeline
                          ? SvgNames.StarFilled
                          : SvgNames.Star
                      }
                      width={24}
                      height={24}
                      color="black"
                    />
                  }
                />
              )}
            </TitleLine>
            <SubtitleLine>
              <AddressText>
                {formatPipelineCardAddress({
                  street: pipelineProspect.street,
                  city: pipelineProspect.city,
                  state: pipelineProspect.state,
                  address: pipelineProspect.address,
                })}
              </AddressText>
              {pipelineProspect?.statusDate && (
                <DaysSinceText>
                  <SvgIcon
                    name={SvgNames.ClockCountdown}
                    width={12}
                    height={12}
                    color={tokens.TINTS_OFF_WHITE_20}
                  />
                  <Typography color={tokens.TINTS_OFF_WHITE_20} size={12}>
                    {daysAgoText}
                  </Typography>
                </DaysSinceText>
              )}
            </SubtitleLine>
            {includeSalesRep && (
              <SalesRepInfo>
                <Typography color={tokens.BRAND_HEROBLUE} size={12}>
                  {pipelineProspect.salesRepName} - Sales Rep
                </Typography>
              </SalesRepInfo>
            )}
            <CommissionSectionContainer>
              {showCommissionPills &&
                (showSpecialistCommissions ? (
                  <CommissionPillContainer>
                    <CommissionPill
                      text={`Est. expert commission: ${formatCurrency(
                        commissionAmount,
                        0,
                      )}`}
                      type="success"
                      hideIcon
                    />
                    <CommissionPill
                      text={`Est. specialist commission: ${formatCurrency(
                        specialistCommissionAmount,
                        0,
                      )}`}
                      type="info"
                      hideIcon
                    />
                  </CommissionPillContainer>
                ) : (
                  <CommissionPill
                    text={`Est. net commission: ${formatCurrency(
                      commissionAmount,
                      0,
                    )}`}
                    type="success"
                    hideIcon
                  />
                ))}
            </CommissionSectionContainer>
          </PipelineCardHeader>
          <AppointmentsSection>
            {canUseAppointments &&
              isPreSale &&
              pipelineProspect.highlightAppointment && (
                <AppointmentView
                  appointment={pipelineProspect.highlightAppointment}
                  timezone={pipelineProspect.timezone}
                />
              )}
            {siteSurveyAppointment && (
              <UpcomingAppointment>
                <HomeReviewLayout>
                  <div>
                    <Flex>
                      <SvgIcon
                        style={{ display: 'flex', marginRight: 4 }}
                        width={16}
                        height={16}
                        color={tokens.TINTS_OFF_WHITE_20}
                        name={SvgNames.Appointments}
                      />{' '}
                      <Typography size={12}>Home Review</Typography>
                    </Flex>

                    <Typography
                      size={12}
                      style={{ display: 'block', marginLeft: 20 }}>
                      {formatTime(siteSurveyAppointment.arrivalWindowStartTime)}{' '}
                      - {formatDate(siteSurveyAppointment.arrivalWindowEndTime)}
                    </Typography>
                  </div>
                  <SurveyPill
                    text={siteSurveyAppointment.status}
                    type="info"
                    hideIcon
                  />
                </HomeReviewLayout>
              </UpcomingAppointment>
            )}
          </AppointmentsSection>
          {userProfile && (
            <StyledPipelineUrgentActionBanner
              pipelineProspect={pipelineProspect}
              oneBaseUrl={oneBaseUrl}
              salesforceBaseUrl={salesforceBaseUrl}
              sfdcEnv={sfdcEnv}
              sfPartnerBaseUrl={sfPartnerBaseUrl}
              splatBaseUrl={splatBaseUrl}
              token={hybridToken}
              tokenType={hybridTokenType}
              showInstallScheduleModalAndGetUrl={handleShowInstallSchedule}
              viewCapNextSteps={viewCapNextSteps}
              viewPendingTask={viewPendingTask}
            />
          )}
          <ActionButtonsContainer>
            <Button
              aria-label={isOpen ? 'Hide details' : 'Show details'}
              onClick={() => setIsOpen((current) => !current)}
              iconMiddle={
                isOpen ? (
                  <SvgIcon name={SvgNames.ChevronUp} />
                ) : (
                  <SvgIcon name={SvgNames.ChevronDown} />
                )
              }
              size="sm"
              color="secondary"
            />
            {(isOwner || splatUserType === 'CX Pro') &&
              pipelineProspect.contactId &&
              (isSalesChatEnabled || splatUserType === 'CX Pro') && (
                <MessagesButtonContainer>
                  <Button onClick={handleChatClick} size="xs" color="secondary">
                    Messages
                  </Button>
                  {numUnreadChats > 0 && (
                    <UnreadMessagesBadge data-testid="unread-messages-badge">
                      {numUnreadChats > 9 ? '9+' : numUnreadChats}
                    </UnreadMessagesBadge>
                  )}
                </MessagesButtonContainer>
              )}
            <Button
              onClick={
                isMobile ? mobileOpenOfferPresentation : openOfferPresentation
              }
              size="xs"
              color="secondary">
              Offer presentation
            </Button>
          </ActionButtonsContainer>
          {isOpen && (
            <PipelineCardBodyContainer>
              {isOwner && (
                <>
                  <PipelineCardInfoItem
                    value="View customer profile"
                    onClick={navigateToCustomerProfile}
                  />
                  {canSendShareBill && (
                    <PipelineCardInfoItem
                      value="Ask customer to share bill"
                      onClick={() => setIsShareBillOpen(true)}
                    />
                  )}
                  {canSendOfferPresentation && (
                    <PipelineCardInfoItem
                      value="Send offer presentation"
                      onClick={() => setIsSharePresentationOpen(true)}
                    />
                  )}
                  <PipelineCardInfoItem
                    value="View opportunity"
                    onClick={viewInSalesforce}
                  />
                  {/* // TODO: Remove this PipelineCardInfoItem once the new offer presentation work 
                  // is ready (SPLAT1-9201). Then, update the `Offer presentation` button above with this CTA */}
                  {isNewPresentationEnabled && (
                    <PipelineCardInfoItem
                      onClick={openNewOfferPresentation}
                      value="View NEW offer presentation"
                    />
                  )}
                </>
              )}
              {projectUrl && (
                <PipelineCardInfoItem
                  value="View project"
                  onClick={viewProject}
                />
              )}
              {pipelineProspect.phone && (
                <PipelineCardInfoItem
                  icon={SvgNames.Call}
                  value={pipelineProspect.phone}
                  valueHref={`tel:${pipelineProspect.phone}`}
                  copyAriaLabel="Copy phone number"
                  onCopy={copyFieldValue}
                  showCopyButton={true}
                />
              )}
              {pipelineProspect.email && (
                <PipelineCardInfoItem
                  icon={SvgNames.Email}
                  value={pipelineProspect.email}
                  onClick={emailCustomer}
                  copyAriaLabel="Copy email"
                  onCopy={copyFieldValue}
                  showCopyButton={true}
                />
              )}
              {!useJobCode && (
                <PipelineCardInfoItem
                  icon={SvgNames.Lightning}
                  label="Prospect ID:"
                  value={pipelineProspect.prospectId}
                  copyAriaLabel="Copy prospect ID"
                  onCopy={copyFieldValue}
                  showCopyButton={true}
                />
              )}
              {useJobCode && (
                <PipelineCardInfoItem
                  icon={SvgNames.Lightning}
                  label="Job code:"
                  value={pipelineProspect.jobCode ?? ''}
                  copyAriaLabel="Copy job code"
                  onCopy={copyFieldValue}
                  showCopyButton={true}
                />
              )}
              {isOwner && isPreSale && (
                <PipelineCardInfoItem
                  value="Mark as closed lost"
                  valueColor={tokens.ERROR_50}
                  onClick={markClosedLost}
                />
              )}
            </PipelineCardBodyContainer>
          )}
        </PipelineStyledCard>
        {createPortal(
          <StyledSlidingDrawer
            isOpen={isChatOpen}
            onClose={() => setIsChatOpen(false)}
            closeOnOutsideClick={true}
            closeOnEscape={true}
            styles={{
              width: isMobile ? '100%' : isTablet ? '75%' : '66%',
              maxWidth: isMobile ? '100vw' : '100%',
              height: isMobile ? '100%' : undefined,
              left: isMobile ? 0 : 'auto',
              right: 0,
            }}>
            {pipelineProspect.contactId && (
              <ChatIframe contactId={pipelineProspect.contactId} drawer />
            )}
          </StyledSlidingDrawer>,
          drawerHost ?? document.body,
        )}
      </>
    );
  },
);

const PipelineStyledCard = styled(Card)`
  width: 303px;
  box-shadow: 0 4px 10px 0 #0000001f;
`;

const PipelineCardHeader = styled(CardHeader)`
  padding: 12px;
  height: auto;
  background: ${tokens.TINTS_CHARGE_BLUE_80};
  align-items: start;
  flex-direction: column;
  min-height: auto;
`;

const TitleLine = styled.div`
  color: ${tokens.BRAND_HEROBLUE};
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  min-width: 0;
`;

const TitleText = styled.span`
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
`;

const SubtitleLine = styled.div`
  text-wrap: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
  color: ${tokens.BRAND_HEROBLUE};
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const AddressText = styled.div`
  text-wrap: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
  font-size: 12px;
`;

const CommissionSectionContainer = styled.div`
  padding-top: 8px;
  width: 100%;

  &:empty {
    padding-top: 0;
  }
`;

const DaysSinceText = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
`;

const ActionButtonsContainer = styled.div`
  margin: 0 !important;
  margin-bottom: 0 !important;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  width: 100%;
  padding: 8px 12px;
  box-sizing: border-box;

  button {
    font-size: 14px;

    > svg {
      width: 16px;
      height: 16px;
    }
  }
`;

const MessagesButtonContainer = styled.div`
  position: relative;
  display: inline-flex;
`;

// Matches CountBadge / NotificationsNavButton unread indicator.
const UnreadMessagesBadge = styled.span`
  position: absolute;
  top: -4px;
  right: -4px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 16px;
  height: 16px;
  padding: 0 4px;
  padding-bottom: 2px;
  box-sizing: border-box;
  border-radius: 999px;
  background: ${tokens.NOTIFICATION_BADGE};
  color: ${tokens.WHITE};
  font-size: 10px;
  font-weight: 600;
  line-height: 1;
`;

const AppointmentsSection = styled.div`
  padding: 8px 12px;
  margin: 0 !important;
  border-bottom: 1px solid ${tokens.TINTS_OFF_WHITE_40};

  &:empty {
    display: none;
  }
`;

const PipelineCardBodyContainer = styled.div`
  padding: 0 !important;
  margin: 0 !important;
`;

const StyledPipelineUrgentActionBanner = styled(PipelineUrgentActionBanner)`
  margin: 0 !important;
  padding: 0 !important;
  border-bottom: 1px solid ${tokens.TINTS_OFF_WHITE_40};

  > dialog {
    border: none;
    border-radius: 0;
    position: static !important;
  }
`;

const Flex = styled.div`
  display: flex;
  align-items: end;
`;

const MenuButton = styled(Button)`
  padding: 0;
  flex-shrink: 0;
`;

const SalesRepInfo = styled.div`
  display: flex;
  justify-content: space-between;
  padding-top: 8px;
`;

const UpcomingAppointment = styled.div`
  color: ${tokens.TINTS_OFF_WHITE_20};
  font-size: 12px;
`;

const HomeReviewLayout = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;

  > div:first-of-type {
    flex-shrink: 0;
  }
`;

const SurveyPill = styled(Pill)`
  padding: 4px 6px !important;
  min-height: auto !important;
  align-self: center;
  flex-shrink: 1;
  min-width: 0;
  width: fit-content;
  max-width: 100%;

  > p {
    line-height: normal;
  }
`;

const CommissionPillContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  width: 100%;
`;

const CommissionPill = styled(Pill)`
  padding: 0.5rem 0.75rem !important;
  width: 100% !important;

  > p {
    line-height: 1.2;
    font-size: 12px;
  }
`;

const StyledSlidingDrawer = styled(SlidingDrawer)`
  margin: 0px !important;
  scrollbar-width: none;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    width: 100% !important;
    max-width: 100vw !important;
    height: 100% !important;
    left: 0 !important;
    right: 0 !important;
  }
`;

export { PipelineCardV2 };
